import { V2_ENDPOINT } from "@/consts";
import type { PaginatedData } from "@/types/pagination";
import { authenticatedFetch } from "./authenticatedFetch";

export interface SmsVerifiedNumber {
  id: string;
  organizationId: string;
  phoneNumber: string;
  verifiedAt: string;
  verifiedBy?: string;
}

export const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

export async function sendSmsCode(phoneNumber: string): Promise<{ expiresAt: string }> {
  const response = await authenticatedFetch<ApiResponse<{ expiresAt: string }>>(
    `${V2_ENDPOINT}/v1/sms/numbers/send-code`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phoneNumber }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to send code: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || "Failed to send verification code");
  }

  return data.data;
}

export interface InitiateVerifyResult {
  code: string;
  sendToPhone: string;
  expiresAt: string;
}

export async function initiateSmsVerify(phoneNumber: string): Promise<InitiateVerifyResult> {
  const response = await authenticatedFetch<ApiResponse<InitiateVerifyResult>>(
    `${V2_ENDPOINT}/v1/sms/numbers/initiate-verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phoneNumber }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to initiate verification: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || "Failed to initiate verification");
  }

  return data.data;
}

export interface VerifyStatusResult {
  phoneNumber: string;
  status: "pending" | "verified" | "none";
}

export async function checkSmsVerifyStatus(phoneNumber: string): Promise<VerifyStatusResult> {
  const params = new URLSearchParams({ phoneNumber });
  const response = await authenticatedFetch<ApiResponse<VerifyStatusResult>>(
    `${V2_ENDPOINT}/v1/sms/numbers/verify-status?${params.toString()}`,
    { showErrorToast: false },
  );

  if (!response.ok) {
    throw new Error(`Failed to check status: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || "Failed to check verification status");
  }

  return data.data;
}

export async function verifySmsNumber(phoneNumber: string, code: string): Promise<SmsVerifiedNumber> {
  const response = await authenticatedFetch<ApiResponse<{ number: SmsVerifiedNumber }>>(
    `${V2_ENDPOINT}/v1/sms/numbers/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phoneNumber, code }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to verify number: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || "Verification failed");
  }

  return data.data.number;
}

export async function listSmsNumbers(
  query?: string,
  limit: number = 50,
  offset: number = 0,
): Promise<PaginatedData<SmsVerifiedNumber>> {
  const params = new URLSearchParams();
  params.append("limit", String(limit));
  params.append("offset", String(offset));
  if (query) {
    params.append("q", query);
  }

  const response = await authenticatedFetch<ApiResponse<PaginatedData<SmsVerifiedNumber>>>(
    `${V2_ENDPOINT}/v1/sms/numbers?${params.toString()}`,
    { showErrorToast: false },
  );

  if (!response.ok) {
    throw new Error(`Failed to list numbers: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(`API error: ${data.message}`);
  }

  return data.data ?? { items: [], limit, offset, hasMore: false };
}

export async function deleteSmsNumber(id: string): Promise<void> {
  const response = await authenticatedFetch<ApiResponse<null>>(`${V2_ENDPOINT}/v1/sms/numbers/${id}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    throw new Error(errorData.message || `Failed to delete number: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.code !== 0) {
    throw new Error(data.message || "Failed to delete verified number");
  }
}
